import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { legalTransitionsFrom, sameRequestType } from '@esa/shared';
import { labelAgents } from '../lib/assignee.js';
import { hasCapability } from '../lib/access.js';
import {
  ArrowRightLeft,
  Check,
  CheckCheck,
  Copy,
  ExternalLink,
  Eye,
  GitMerge,
  Inbox,
  Link2,
  PauseCircle,
  RefreshCw,
  Trash2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { api, type Me, type TenantConfig, type TicketDetail, type TicketState } from '../lib/api.js';
import { LinkTicketDialog } from './TicketLinks.js';
import { ManageDialog, manageAbilities, type ManageKind } from './TicketManage.js';
import type { TicketPriority } from '@esa/shared';
import { STATE_LABEL } from '../lib/format.js';
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Chilli,
  PRIORITY_OPTIONS,
  Select,
  Textarea,
  useToast,
} from '../ui/index.js';

const PENDING: TicketState[] = [
  'pending_requester',
  'pending_external',
  'pending_internal',
  'pending_event',
];

/** Every state an agent can move a ticket to from the menu. */
const QUICK_STATES: TicketState[] = [
  'open',
  'pending_requester',
  'pending_internal',
  'pending_external',
  'pending_event',
  'resolved',
];

/**
 * A date input wants `YYYY-MM-DD` in the reader's own timezone.
 *
 * Built from the local parts rather than `toISOString().slice(0,10)`, which
 * is UTC: an agent in California opening this after 4pm would be offered
 * tomorrow, and would not notice until a ticket came back a day early.
 */
function dateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Local midnight-ish on the chosen day; the sweep compares instants. */
function endOfLocalDay(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 23, 59, 0, 0);
}

export interface TicketTarget {
  number: number;
  state: TicketState;
  /** So the menu can tick the current one instead of making you remember it. */
  priority: TicketPriority;
  assigneeOid: string | null;
  /** The queue it is in, so the menu can tick it. */
  queueKey?: string | null;
}

/**
 * Ticket actions, shared by the right-click menu and the board's drop
 * handler.
 *
 * The reason prompt lives here because the API refuses a pending transition
 * without one — so dragging a card onto "Blocked externally" has to ask who
 * we are waiting on, exactly as the form does. The rule is enforced once, on
 * the server, and surfaced everywhere it applies.
 */
export function useTicketActions(tenant: string, config?: TenantConfig, me?: Me) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<{ target: TicketTarget; to: TicketState } | null>(null);
  const [reason, setReason] = useState('');
  const [party, setParty] = useState('');
  // What a resolve needs. `selfSolvable` starts null, not false: the API
  // wants an answer and a default would invent one.
  const [fix, setFix] = useState('');
  const [cause, setCause] = useState('');
  const [selfSolvable, setSelfSolvable] = useState<boolean | null>(null);
  const [closeValues, setCloseValues] = useState<Record<string, string>>({});
  // The event wait. `eventKey` starts empty so the reader picks deliberately
  // rather than accepting whichever event happened to sort first.
  const [eventKey, setEventKey] = useState('');
  const [eventDate, setEventDate] = useState('');
  /** The ticket whose links are being edited, or null when the dialog is shut. */
  const [relating, setRelating] = useState<TicketTarget | null>(null);
  /** A move into a restricted queue waiting on its confirmation, or null. */
  const [confidentialMove, setConfidentialMove] = useState<{ number: number; queueKey: string; name: string } | null>(
    null,
  );
  /** Merge / transfer / delete, opened from the menu, or null. */
  const [managing, setManaging] = useState<{ kind: ManageKind; number: number } | null>(null);
  /** What this desk waits on. Empty when nobody has configured any. */
  const events = config?.eventTypes ?? [];

  // The ticket this dialog is about, fetched only while it is open. Needed
  // for two things the field definitions alone cannot answer: which request
  // type this is, and which required fields already carry a value.
  const resolving = pending?.to === 'resolved';
  const detail = useQuery({
    queryKey: ['ticket', tenant, pending?.target.number],
    queryFn: () =>
      api.get<TicketDetail>(`/api/v1/tenants/${tenant}/tickets/${pending!.target.number}`),
    enabled: Boolean(resolving && pending),
  });

  /**
   * What this close is still missing.
   *
   * Mirrors the server's own check — required at close, in scope for the
   * request type, and not already answered. Asking for anything else would
   * be demanding what will not be validated; asking for less would put the
   * rejection back.
   */
  const outstanding = (() => {
    if (!resolving) return [];
    const t = detail.data?.ticket;
    const current = (t?.custom ?? {}) as Record<string, unknown>;
    return (config?.fields ?? []).filter((f) => {
      if (f.requiredAt !== 'close') return false;
      if (f.visibility === 'requester') return false;
      const scoped = f.requestTypeKeys;
      if (
        scoped &&
        scoped.length > 0 &&
        !scoped.some((k) => sameRequestType(k, t?.request_type ?? null, config?.requestTypes ?? []))
      ) {
        return false;
      }
      const v = current[f.key];
      return v === undefined || v === null || v === '';
    });
  })();

  const missingClose = outstanding.some((f) => !(closeValues[f.key] ?? '').trim());

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['tickets'] });
    void qc.invalidateQueries({ queryKey: ['queue-counts', tenant] });
    void qc.invalidateQueries({ queryKey: ['ticket'] });
  };

  const setState = useMutation({
    mutationFn: (v: {
      number: number;
      to: TicketState;
      reason?: string;
      blockedParty?: string;
      fix?: string;
      cause?: string;
      selfSolvable?: boolean;
      custom?: Record<string, unknown>;
      eventTypeKey?: string;
      eventDueAt?: string;
    }) =>
      api.post(`/api/v1/tenants/${tenant}/tickets/${v.number}/state`, {
        state: v.to,
        reason: v.reason,
        blockedParty: v.blockedParty,
        fix: v.fix,
        cause: v.cause,
        selfSolvable: v.selfSolvable,
        custom: v.custom,
        eventTypeKey: v.eventTypeKey,
        eventDueAt: v.eventDueAt,
      }),
    onSuccess: (_d, v) => {
      invalidate();
      toast.success(`#${v.number} → ${STATE_LABEL[v.to]}`);
      setPending(null);
      setReason('');
      setParty('');
      setFix('');
      setCause('');
      setSelfSolvable(null);
      setCloseValues({});
      setEventKey('');
      setEventDate('');
    },
    onError: (e) => toast.error('Transition rejected', e instanceof Error ? e.message : undefined),
  });

  /**
   * Priority, from the row.
   *
   * The same endpoint the ticket screen uses. Set here because this is where
   * somebody actually decides it — scanning a queue, seeing three things that
   * matter more than the fourth.
   */
  const setPriority = useMutation({
    mutationFn: (v: { number: number; priority: TicketPriority }) =>
      api.patch(`/api/v1/tenants/${tenant}/tickets/${v.number}`, { priority: v.priority }),
    onSuccess: (_d, v) => {
      invalidate();
      toast.success(
        v.priority === 'critical'
          ? `#${v.number} is critical — pinned to the top of the queue`
          : `#${v.number} set to ${v.priority}`,
      );
    },
    onError: (e) => toast.error('Could not set priority', e instanceof Error ? e.message : undefined),
  });

  const setQueue = useMutation({
    mutationFn: (v: { number: number; queueKey: string; name: string }) =>
      api.patch(`/api/v1/tenants/${tenant}/tickets/${v.number}`, { queueKey: v.queueKey }),
    onSuccess: (_d, v) => {
      invalidate();
      const restricted = config?.queues.find((q) => q.key === v.queueKey)?.restricted;
      toast.success(`#${v.number} moved to ${v.name}`, restricted ? 'Only its members can see it now.' : undefined);
      setConfidentialMove(null);
    },
    onError: (e) => toast.error('Could not move it', e instanceof Error ? e.message : undefined),
  });

  /**
   * The menu's queue move. A restricted queue (072) is confirmed first, as on
   * the ticket page: anyone who is not a member, the mover included, loses
   * sight of the ticket the moment it lands.
   */
  const moveToQueue = (number: number, queueKey: string) => {
    const qu = config?.queues.find((q) => q.key === queueKey);
    if (!qu) return;
    if (qu.restricted) setConfidentialMove({ number, queueKey, name: qu.name });
    else setQueue.mutate({ number, queueKey, name: qu.name });
  };

  /**
   * Run routing again (punch list #14): for an admin who has reworded a
   * queue's description and wants to see whether a misplaced ticket now
   * lands where it belongs. The answer says what happened and why.
   */
  const reroute = useMutation({
    mutationFn: (v: { number: number }) =>
      api.post<{ moved: boolean; keptConfidential: boolean; to: { name: string } | null; note: string; modelRead: boolean }>(
        `/api/v1/tenants/${tenant}/tickets/${v.number}/reroute`,
        {},
      ),
    onSuccess: (r, v) => {
      invalidate();
      const title = r.keptConfidential
        ? `#${v.number} stays where it is`
        : r.moved
          ? `#${v.number} moved to ${r.to?.name ?? 'another queue'}`
          : `#${v.number} stays where it is`;
      toast.success(title, r.modelRead ? r.note : `${r.note} (No model was available to read it, so only the routing rules were checked.)`);
    },
    onError: (e) => toast.error('Could not re-run routing', e instanceof Error ? e.message : undefined),
  });

  const assign = useMutation({
    mutationFn: (v: { number: number; oid: string | null }) =>
      api.patch(`/api/v1/tenants/${tenant}/tickets/${v.number}`, { assigneeOid: v.oid }),
    onSuccess: (_d, v) => {
      invalidate();
      toast.success(v.oid ? `#${v.number} assigned` : `#${v.number} unassigned`);
    },
    onError: (e) => toast.error('Could not reassign', e instanceof Error ? e.message : undefined),
  });

  /**
   * Ask for whatever the target state needs before attempting it.
   *
   * The alternative — send it and surface the rejection — is what this
   * replaces. The server's requirements are not a surprise; they can be
   * asked for.
   */
  const NEEDS_INPUT = (to: TicketState) => PENDING.includes(to) || to === 'resolved';

  const move = (target: TicketTarget, to: TicketState) => {
    if (to === target.state) return;
    if (NEEDS_INPUT(to)) {
      setPending({ target, to });
      return;
    }
    setState.mutate({ number: target.number, to });
  };

  const reasonDialog = (
    <Dialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)}>
      {pending && (
        <DialogContent
          width="sm"
          title={`Move #${pending.target.number} to ${STATE_LABEL[pending.to]}`}
          description={
            pending.to === 'resolved'
              ? 'Resolving records what fixed it. This is what the next person who hits the same problem reads, and what an article gets drafted from.'
              : 'A waiting state needs a reason — it is the only input to the blocked-time report, so the API will not accept the change without it.'
          }
        >
          {pending.to === 'resolved' ? (
            <div className="space-y-3">
              <Field
                label="What fixed it?"
                required
                hint="Not the closing reply — that usually just says “all sorted now”."
              >
                <Textarea
                  autoFocus
                  rows={3}
                  value={fix}
                  onChange={(e) => setFix(e.target.value)}
                  placeholder="Re-granted Editor on /Shared/Firmwide/CAD Services; the group sync had dropped them."
                />
              </Field>
              <Field label="What was the cause?" hint="Only where it differs from what was reported.">
                <Input
                  value={cause}
                  onChange={(e) => setCause(e.target.value)}
                  placeholder="Nightly group sync removed the grant"
                />
              </Field>
              {/* WHAT THIS GROUP REQUIRES AT CLOSE. Rendered from the same
                  definitions the server validates against — a required field
                  with no box was the whole of "Resolution code is required".
                  Absent for a group that requires nothing. */}
              {outstanding.map((f) => (
                <Field key={f.key} label={f.label} required>
                  {f.validation.options?.length ? (
                    <Select
                      value={closeValues[f.key] ?? ''}
                      onValueChange={(v) => setCloseValues((c) => ({ ...c, [f.key]: v }))}
                      placeholder="Choose…"
                      options={f.validation.options.map((o) => ({ value: o, label: o }))}
                    />
                  ) : (
                    <Input
                      value={closeValues[f.key] ?? ''}
                      onChange={(e) => setCloseValues((c) => ({ ...c, [f.key]: e.target.value }))}
                    />
                  )}
                </Field>
              ))}

              {/* No default. Whether a requester-facing article is worth
                  writing turns on this, and a pre-selected answer is an
                  answer nobody gave. */}
              <Field label="Could the requester have done this themselves?" required>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={selfSolvable === true ? 'primary' : 'secondary'}
                    onClick={() => setSelfSolvable(true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={selfSolvable === false ? 'primary' : 'secondary'}
                    onClick={() => setSelfSolvable(false)}
                  >
                    No — needed an agent
                  </Button>
                </div>
              </Field>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Why is it waiting?" required>
                <Input
                  autoFocus
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Waiting on a vendor quote"
                />
              </Field>
              {pending.to === 'pending_external' && (
                <Field label="Who are we waiting on?" required>
                  <Input
                    value={party}
                    onChange={(e) => setParty(e.target.value)}
                    placeholder="Dell, the client, the county…"
                  />
                </Field>
              )}
              {pending.to === 'pending_event' && (
                <>
                  <Field label="What are we waiting for?" required>
                    <Select
                      value={eventKey}
                      placeholder="Choose an event…"
                      options={events.map((ev) => ({
                        value: ev.key,
                        label: ev.name,
                        hint: ev.description ?? undefined,
                      }))}
                      onValueChange={(key) => {
                        setEventKey(key);
                        // Prefill from the event's usual length, if it has
                        // one. Only when the reader has not already typed a
                        // date — overwriting their answer with a default is
                        // how a form argues with the person filling it in.
                        const chosen = events.find((x) => x.key === key);
                        if (chosen?.defaultDays && !eventDate) {
                          const d = new Date();
                          d.setDate(d.getDate() + chosen.defaultDays);
                          setEventDate(dateInputValue(d));
                        }
                      }}
                    />
                  </Field>
                  <Field label="Bring it back on" required>
                    <Input
                      type="date"
                      value={eventDate}
                      min={dateInputValue(new Date())}
                      onChange={(e) => setEventDate(e.target.value)}
                    />
                  </Field>
                  <p className="text-2xs leading-relaxed text-ink-tertiary">
                    The SLA clock stops, and it comes back to you flagged on the day. It
                    stays visible under Blocked in the meantime.
                  </p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={setState.isPending}
              disabled={
                pending.to === 'resolved'
                  ? // Mirrors the schema: `fix` is min(4) after trimming,
                    // selfSolvable must have been answered either way, and
                    // the group's required-at-close fields must be filled.
                    fix.trim().length < 4 || selfSolvable === null || missingClose || detail.isLoading
                  : !reason ||
                    (pending.to === 'pending_external' && !party) ||
                    // The server refuses an event wait with no event or no
                    // date, so the button does too rather than producing it.
                    (pending.to === 'pending_event' && (!eventKey || !eventDate))
              }
              onClick={() =>
                setState.mutate(
                  pending.to === 'resolved'
                    ? {
                        number: pending.target.number,
                        to: pending.to,
                        fix: fix.trim(),
                        cause: cause.trim() || undefined,
                        selfSolvable: selfSolvable ?? undefined,
                        custom: outstanding.length
                          ? Object.fromEntries(
                              outstanding.map((f) => [f.key, (closeValues[f.key] ?? '').trim()]),
                            )
                          : undefined,
                      }
                    : {
                        number: pending.target.number,
                        to: pending.to,
                        reason,
                        blockedParty: party || undefined,
                        eventTypeKey: eventKey || undefined,
                        eventDueAt: eventDate ? endOfLocalDay(eventDate).toISOString() : undefined,
                      },
                )
              }
            >
              {pending.to === 'resolved'
                ? 'Resolve ticket'
                : pending.to === 'pending_event'
                  ? 'Park until then'
                  : 'Move ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );

  /**
   * The link picker, rendered once for the page.
   *
   * Mounted here rather than inside the context menu: a menu unmounts the
   * moment an item is chosen, and a dialog mounted inside it would close
   * itself along with the menu that opened it.
   */
  const linkDialog = relating ? (
    <LinkTicketDialog
      tenant={tenant}
      number={String(relating.number)}
      open
      onOpenChange={(v) => !v && setRelating(null)}
      onLinked={invalidate}
    />
  ) : null;

  // Mounted here for the same reason as the link picker.
  const queueDialog = (
    <Dialog open={confidentialMove !== null} onOpenChange={(o) => !o && setConfidentialMove(null)}>
      {confidentialMove && (
        <DialogContent
          width="sm"
          title={`Move #${confidentialMove.number} to ${confidentialMove.name}?`}
          description="Only its members will be able to see this ticket. If you are not a member, you will lose sight of it straight away."
        >
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfidentialMove(null)}>
              Cancel
            </Button>
            <Button
              loading={setQueue.isPending}
              onClick={() =>
                setQueue.mutate({
                  number: confidentialMove.number,
                  queueKey: confidentialMove.queueKey,
                  name: confidentialMove.name,
                })
              }
            >
              Move it
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );

  const manageDialog = managing ? (
    <ManageDialog
      kind={managing.kind}
      tenant={tenant}
      number={managing.number}
      me={me}
      onClose={() => setManaging(null)}
      onDone={invalidate}
    />
  ) : null;

  return {
    move,
    assign,
    setPriority,
    setQueue,
    moveToQueue,
    reroute,
    /** Re-running routing is for whoever administers the group's configuration. */
    canReroute: hasCapability(me, tenant, 'config.write'),
    /** Moving between queues is its own capability (queue lead by default). */
    canMoveQueue: hasCapability(me, tenant, 'ticket.queue.move'),
    queues: config?.queues ?? [],
    relate: setRelating,
    manage: (kind: ManageKind, target: TicketTarget) => setManaging({ kind, number: target.number }),
    /** What this person may do from the menu's last group. */
    can: manageAbilities(me, tenant),
    reasonDialog,
    linkDialog,
    queueDialog,
    manageDialog,
    isMoving: setState.isPending,
  };
}

/**
 * Wraps any element in a right-click menu of ticket actions.
 */
export function TicketContextMenu({
  tenant,
  target,
  config,
  meOid,
  actions,
  onPeek,
  children,
}: {
  tenant: string;
  target: TicketTarget;
  config: TenantConfig | undefined;
  meOid: string | undefined;
  actions: ReturnType<typeof useTicketActions>;
  onPeek?: () => void;
  children: ReactNode;
}) {
  const nav = useNavigate();
  const toast = useToast();

  const copyLink = () => {
    const url = `${window.location.origin}/t/${tenant}/${target.number}`;
    void navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Could not copy'));
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuLabel>Ticket #{target.number}</ContextMenuLabel>

        {onPeek && (
          <ContextMenuItem icon={<Eye />} onSelect={onPeek} shortcut="↵">
            Preview
          </ContextMenuItem>
        )}
        <ContextMenuItem
          icon={<ExternalLink />}
          onSelect={() => nav(`/t/${tenant}/${target.number}`)}
          shortcut="O"
        >
          Open full ticket
        </ContextMenuItem>
        <ContextMenuItem icon={<Link2 />} onSelect={copyLink}>
          Copy link
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuSub>
          <ContextMenuSubTrigger icon={<ArrowRightLeft />}>Move to</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {/* Only what this ticket can actually move to. Offering the
                rest meant the menu proposed transitions the state machine
                would refuse, and you found out by clicking. */}
            {QUICK_STATES.filter(
              (s) => s !== target.state && legalTransitionsFrom(target.state).includes(s),
            ).map((s) => (
              <ContextMenuItem
                key={s}
                icon={PENDING.includes(s) ? <PauseCircle /> : <CheckCheck />}
                onSelect={() => actions.move(target, s)}
              >
                {STATE_LABEL[s]}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>

        {/* Beside Move and Priority because it is the same kind of act:
            something you decide about a ticket while scanning a list, not
            something you open it to do. */}
        <ContextMenuItem icon={<Link2 />} onSelect={() => actions.relate(target)}>
          Relate to…
        </ContextMenuItem>

        <ContextMenuSub>
          <ContextMenuSubTrigger icon={<Chilli />}>Priority</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {/* The current one is shown ticked rather than hidden: a menu
                that omits the current value makes you remember what it was
                to know whether you are changing anything. */}
            {PRIORITY_OPTIONS.map((o) => (
              <ContextMenuItem
                key={o.value}
                icon={o.value === target.priority ? <Check /> : undefined}
                onSelect={() =>
                  o.value !== target.priority &&
                  actions.setPriority.mutate({ number: target.number, priority: o.value })
                }
              >
                {o.label}
                {o.value === 'critical' ? ' — pins to top' : ''}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>

        {/* Out of Triage without opening the ticket: the same act as setting
            its priority, decided while scanning the list. */}
        {actions.canMoveQueue && actions.queues.length > 1 && (
          <ContextMenuSub>
            <ContextMenuSubTrigger icon={<Inbox />}>Move to queue</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {actions.queues.map((qu) => (
                <ContextMenuItem
                  key={qu.key}
                  icon={qu.key === target.queueKey ? <Check /> : undefined}
                  onSelect={() => qu.key !== target.queueKey && actions.moveToQueue(target.number, qu.key)}
                >
                  {qu.restricted ? `${qu.name} (restricted)` : qu.name}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}

        {actions.canReroute && (
          <ContextMenuItem icon={<RefreshCw />} onSelect={() => actions.reroute.mutate({ number: target.number })}>
            Re-run routing
          </ContextMenuItem>
        )}

        <ContextMenuSub>
          <ContextMenuSubTrigger icon={<UserCheck />}>Assign to</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {meOid && (
              <ContextMenuItem
                icon={<UserCheck />}
                onSelect={() => actions.assign.mutate({ number: target.number, oid: meOid })}
              >
                Me
              </ContextMenuItem>
            )}
            <ContextMenuItem
              icon={<UserX />}
              onSelect={() => actions.assign.mutate({ number: target.number, oid: null })}
            >
              Nobody
            </ContextMenuItem>
            <ContextMenuSeparator />
            {/* Labelled through the same function as the owner picker, so
                two accounts belonging to one person cannot appear here as
                two identical lines. */}
            {labelAgents(config?.agents ?? [])
              .slice(0, 12)
              .map((a) => (
                <ContextMenuItem
                  key={a.oid}
                  onSelect={() => actions.assign.mutate({ number: target.number, oid: a.oid })}
                >
                  {a.label}
                </ContextMenuItem>
              ))}
          </ContextMenuSubContent>
        </ContextMenuSub>

        {/* The same three as the ticket's More button, each only for whoever
            may do it on this desk. */}
        {(actions.can.merge || actions.can.transfer || actions.can.delete) && <ContextMenuSeparator />}
        {actions.can.merge && (
          <ContextMenuItem icon={<GitMerge />} onSelect={() => actions.manage('merge', target)}>
            Merge into another ticket…
          </ContextMenuItem>
        )}
        {actions.can.transfer && (
          <ContextMenuItem icon={<ArrowRightLeft />} onSelect={() => actions.manage('transfer', target)}>
            Transfer to another desk…
          </ContextMenuItem>
        )}
        {actions.can.delete && (
          <ContextMenuItem icon={<Trash2 />} danger onSelect={() => actions.manage('delete', target)}>
            Delete ticket…
          </ContextMenuItem>
        )}

        <ContextMenuSeparator />
        <ContextMenuItem
          icon={<Copy />}
          onSelect={() => {
            void navigator.clipboard?.writeText(`#${target.number}`);
            toast.success('Reference copied');
          }}
        >
          Copy reference
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
